import { getProjectsListForSeries, setProjectsListForSeriesOptions } from "@/store/actions/admin/events";
import { loadCurrentSeriesForProject, setSeriesEvents } from "@/store/actions/admin/series";
import { useAppDispatch, useThunkDispatch, useTypedSelector } from "@/store/reducers/use-typed-selector";
import { selectCurrentChannel } from "@/store/selectors/authentication";
import { selectCurrentSeries } from "@/store/selectors/series";
import { eventEnd } from "@/store/utils/event-end";
import { eventStart } from "@/store/utils/event-start";
import { SessionPanelLayoutsTypes, type SelectedBrandliveEvent, type Session } from "@/types/working-model";
import { useGetAdminUrl } from "@/utils/admin-routing-utils";
import { useEventUuidParam } from "@/utils/use-event-uuid-param";
import TextInput from "@general-ui/text-input/text";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { SessionPanelMap } from "../session-panel-route-map";
import { Virtuoso } from "react-virtuoso";
import WaitingIndicator from "@general-ui/waiting-indicator/waiting-indicator";
import classNames from "classnames";
import { showAlert } from "@general-ui/alert/alert-service";
import Image from '@general-ui/image/image';
import { niceDate, resizedImage } from "@/utils/utils";
import Icon, { COLORS, ICONS } from "@general-ui/icon";
import { durationBetweenTimestamps } from "@/utils/date-utils";
import images from "@/images";
import "./events-list.scss";

const projectsArrayToMap = (arr: SelectedBrandliveEvent[]) => {
	return new Map(arr.map((ev) => [ev.uuid, ev]));
};

export default function EventsList() {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const projectUuidParam = useEventUuidParam();
	const channel = useTypedSelector(selectCurrentChannel);
	const getProjectOptions = useTypedSelector(state => state.EventsReducer.getProjectsListForSeriesOptions);
	const dispatch = useThunkDispatch();
	const projects = useTypedSelector(state => state.EventsReducer.projectsListForSeries);
	const loading = useTypedSelector(state => state.EventsReducer.projectsLoading);
	const error = useTypedSelector(state => state.EventsReducer.projectsError);
	const channelRef = useRef(channel?.channel);
	const projectsMap = useRef<Map<string, SelectedBrandliveEvent>>(projectsArrayToMap(projects));
	const currentSeries = useTypedSelector(selectCurrentSeries);
	const [page, setPage] = useState(0);
	const [selectedProjects, setSelectedProjects] = useState<string[]>(currentSeries?.events.map((ev) => ev.uuid) ?? []);
	const [busy, setBusy] = useState(false);
	const history = useHistory();
	const adminPath = useGetAdminUrl();
	const searchDebounce = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		setSelectedProjects(currentSeries?.events.map((ev) => ev.uuid) ?? []);
	}, [currentSeries]);

	useEffect(() => {
		channelRef.current = channel?.channel;
	}, [channel]);

	useEffect(() => {
		projectsMap.current = projectsArrayToMap(projects);
	}, [projects]);

	const search = useCallback((searchTerm: string) => {
		if (searchTerm.trim()) {
			if (searchDebounce.current) clearTimeout(searchDebounce.current);
			searchDebounce.current = setTimeout(() => {
				dispatch(
					setProjectsListForSeriesOptions({
						searchCriteria: searchTerm.trim(),
						append: false,
						offset: 0,
					})
				);
				setPage(0);
			}, 750);
		} else {
			dispatch(
				setProjectsListForSeriesOptions({
					searchCriteria: undefined,
					append: false,
				})
			);
			setPage(0);
		}
	}, [dispatch]);

	const handleSearch = (e: React.FocusEvent<HTMLInputElement, Element>) => {
		if (!channelRef.current) return;
		const searchValue = e.target.value;
		search(searchValue);
	};

	useEffect(() => {
		if (!channelRef.current) return;

		dispatch(
			setProjectsListForSeriesOptions({
				offset: page * 15,
				append: true,
				pageSize: 15,
			})
		);
	}, [dispatch, page]);

	useEffect(() => {
		if (channelRef.current) {
			console.log({ ...getProjectOptions, channel: channelRef.current });
			dispatch(getProjectsListForSeries({ ...getProjectOptions, channel: channelRef.current }));
		}
	}, [dispatch, getProjectOptions]);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!channelRef.current) return;
		const searchValue = e.target.value;
		search(searchValue);
	};

	const handleEnter = (searchValue: string) => {
		if (!channelRef.current) return;
		search(searchValue);
	};

	const setProjects = useCallback(async () => {
		try {
			if (!currentSeries) return;

			setBusy(true);
			await dispatch(
				setSeriesEvents({
					channel: currentSeries.channel,
					seriesUuid: currentSeries.uuid,
					eventUuids: selectedProjects,
					eventData: selectedProjects.map((uuid) => {
						const proj = projectsMap.current.get(uuid);

						return {
							uuid: proj?.uuid || '',
							title: proj?.sessions[0].title ?? { base: '', changed: '' },
							timestamp: proj ? eventStart(proj) : 0,
							end_timestamp: proj ? eventEnd(proj) : 0,
							event_image: proj?.event_image,
						};
					}),
				})
			);
			if (projectUuidParam) {
				await dispatch(
					loadCurrentSeriesForProject({
						channel: currentSeries.channel,
						eventUuid: projectUuidParam,
					})
				);
			}
			history.push(adminPath({
				path: SessionPanelMap[SessionPanelLayoutsTypes.UpNext],
			}),
				{
					panelTitle: 'Up Next'
				});
		} catch (e) {
			console.error(e);
		} finally {
			setBusy(false);
		}
	}, [adminPath, currentSeries, dispatch, history, projectUuidParam, selectedProjects]);

	const toggleSelected = useCallback((projectUuid: string, on: boolean) => {
		if (on) {
			setSelectedProjects((prev) => [...prev, projectUuid]);
		} else {
			setSelectedProjects((prev) => prev.filter((uuid) => uuid !== projectUuid));
		}
	}, []);

	return (
		<div
			className="session-panel series-events"
			ref={containerRef}
		>
			<div className="session-panel-header-options">
				<TextInput
					defaultValue={''}
					onChange={handleSearchChange}
					onBlur={handleSearch}
					onEnterKey={handleEnter}
					placeholder="Search..."
					className="small"
					isAdmin
				/>
			</div>
			<div
				className="session-panel-content series-events-list"
			>
				<Virtuoso
					data={projects}
					style={{ height: '100%' }}
					atBottomStateChange={(atBottom) => {
						if (atBottom) {
							setPage((prev) => prev + 1);
						}
					}}
					components={{
						Footer: () => (
							<div className="projects-list-footer">
								<div className="loading">
									{loading && <WaitingIndicator />}
								</div>
							</div>
						),
					}}
					itemContent={(_, project) => {
						let cardImage = project.event_image;

						if (!cardImage) {
							const firstSessionWithImage = [...project.sessions]
								.sort((a: Session, b: Session) => ((a.timestamp || 0) > (b.timestamp || 0) ? 1 : -1))
								.find((session: Session) => session.post_broadcast_thumbnail || session.pre_broadcast_thumbnail);
							cardImage = firstSessionWithImage
								? firstSessionWithImage.post_broadcast_thumbnail ||
								firstSessionWithImage.pre_broadcast_thumbnail ||
								undefined
								: images.TemplatesFallbackImage;
						}

						if (!currentSeries) return <></>;

						const selected = selectedProjects.includes(project.uuid);
						const inAnotherSeries = project.series_uuid && project.series_uuid !== currentSeries.uuid;
						const tooManySessions = project.sessions.length > 1;

						return (
							<div className='add-project-row-container'>
								<div
									className={classNames(`add-project-row`, {
										selected,
										disabled: inAnotherSeries || tooManySessions
									})}
									onClick={() => {
										if (inAnotherSeries) {
											showAlert({ message: 'Project is already in another series', type: 'warning' });
											return;
										}

										if (tooManySessions) {
											showAlert({ message: 'Multi-session projects cannot be added to a series', type: 'warning' });
											return;
										}

										toggleSelected(project.uuid, !selected);
									}}
								>
									{cardImage && (
										<Image
											fallback={cardImage}
											src={resizedImage(cardImage, { height: 40, width: 72, fit: 'cover' })}
										/>
									)}
									<div className="project-metadata">
										<span className="S3">
											{project.name}
										</span>
										{project.sessions[0].timestamp && (
											<time className="C1">
												{niceDate(eventStart(project), 'short')}
											</time>
										)}
									</div>
									<div className={'duration'}>
										<Icon name={ICONS.CLOCK} size={12} color={COLORS.WHITE} />
										<span className="C1">{durationBetweenTimestamps(eventStart(project), eventEnd(project))}</span>
									</div>
								</div>
							</div>
						);
					}}
				/>
			</div>
			<div className="session-panel-footer">
				<button disabled={busy} className="L2" onClick={history.goBack}>Cancel</button>
				<button disabled={busy} className="L2 primary" onClick={setProjects}>Add</button>
			</div>
		</div>
	);
}